// Tool Executor Service
// Serviço para executar ferramentas durante o chat do assistente

import { AssistantToolConfig, ToolCallResult } from '../types/assistant';
import { AVAILABLE_TOOLS, Tool, ToolOperation } from '../types/tools';
import { googleCalendarService } from './google-calendar-service';
import { googleDriveService } from './google-drive-service';
import { saiposService } from './saipos-service';

interface ToolExecutionContext {
  userId: string;
  assistantTools: AssistantToolConfig[];
}

class ToolExecutorService {
  // Verificar se uma operação está habilitada para o assistente
  isOperationEnabled(
    toolId: string, 
    operationId: string, 
    assistantTools: AssistantToolConfig[]
  ): boolean {
    const toolConfig = assistantTools.find(t => t.toolId === toolId);
    if (!toolConfig || !toolConfig.isActive) return false;
    return toolConfig.enabledOperations.includes(operationId);
  }

  // Obter as ferramentas disponíveis para um assistente
  getAvailableTools(assistantTools: AssistantToolConfig[]): Tool[] {
    return AVAILABLE_TOOLS.filter(tool => {
      const config = assistantTools.find(t => t.toolId === tool.id);
      return config && config.isActive;
    }).map(tool => {
      const config = assistantTools.find(t => t.toolId === tool.id)!;
      return {
        ...tool,
        operations: tool.operations.filter(op => 
          config.enabledOperations.includes(op.id)
        )
      };
    });
  }

  // Gerar descrição das ferramentas para o prompt da IA
  generateToolsPrompt(assistantTools: AssistantToolConfig[]): string {
    const availableTools = this.getAvailableTools(assistantTools);
    
    console.log('🔧 generateToolsPrompt:', {
      inputTools: assistantTools,
      availableTools: availableTools.map(t => ({ id: t.id, ops: t.operations.map(o => o.id) }))
    });
    
    if (availableTools.length === 0) {
      console.log('🔧 Nenhuma ferramenta disponível');
      return '';
    }

    // Obter data e hora atual
    const now = new Date();
    const dateOptions: Intl.DateTimeFormatOptions = { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      timeZone: 'America/Sao_Paulo'
    };
    const timeOptions: Intl.DateTimeFormatOptions = { 
      hour: '2-digit', 
      minute: '2-digit',
      timeZone: 'America/Sao_Paulo'
    };
    const currentDate = now.toLocaleDateString('pt-BR', dateOptions);
    const currentTime = now.toLocaleTimeString('pt-BR', timeOptions);
    const isoDate = now.toISOString().split('T')[0]; // YYYY-MM-DD
    
    let prompt = '\n\n';
    prompt += '📅 DATA E HORA ATUAL:\n';
    prompt += `   Hoje é: ${currentDate}\n`;
    prompt += `   Hora atual: ${currentTime} (Horário de Brasília)\n`;
    prompt += `   Data ISO: ${isoDate}\n\n`;
    
    prompt += '🚨🚨🚨 ATENÇÃO MÁXIMA - LEIA ANTES DE RESPONDER 🚨🚨🚨\n\n';
    prompt += '═'.repeat(80) + '\n';
    
    // Detectar quais ferramentas estão disponíveis
    const hasCalendar = availableTools.some(t => t.id === 'google_calendar');
    const hasSaipos = availableTools.some(t => t.id === 'saipos');
    const hasDrive = availableTools.some(t => t.id === 'google_drive');
    
    if (hasCalendar) {
      prompt += '🔧 VOCÊ TEM FERRAMENTAS REAIS PARA ACESSAR O GOOGLE CALENDAR DO USUÁRIO!\n';
    }
    if (hasSaipos) {
      prompt += '🍽️ VOCÊ TEM FERRAMENTAS REAIS PARA ACESSAR O SAIPOS (VENDAS, ESTOQUE, FINANÇAS)!\n';
    }
    if (hasDrive) {
      prompt += '📁 VOCÊ TEM FERRAMENTAS REAIS PARA ACESSAR O GOOGLE DRIVE DO USUÁRIO!\n';
    }
    
    prompt += '═'.repeat(80) + '\n\n';
    
    if (hasCalendar) {
      prompt += '⛔ PROIBIDO: Dizer para o usuário acessar o Google Calendar manualmente\n';
      prompt += '⛔ PROIBIDO: Responder que não tem acesso ao calendário\n';
    }
    if (hasSaipos) {
      prompt += '⛔ PROIBIDO: Dizer que não tem acesso à API do Saipos\n';
      prompt += '⛔ PROIBIDO: Pedir para o usuário acessar o Saipos manualmente\n';
      prompt += '⛔ PROIBIDO: Dizer "não tenho acesso real à API da Saipos"\n';
    }
    prompt += '⛔ PROIBIDO: Gerar arquivos HTML, PDF, CSV ou qualquer arquivo para download\n';
    prompt += '⛔ PROIBIDO: Criar links de download ou botões de download\n\n';
    
    prompt += '✅ OBRIGATÓRIO: Usar as ferramentas abaixo para EXECUTAR a ação diretamente!\n\n';
    
    prompt += 'FORMATO PARA USAR UMA FERRAMENTA (copie exatamente):\n\n';
    if (hasCalendar) {
      prompt += '```tool\n{\n  "tool": "google_calendar",\n  "operation": "OPERACAO_AQUI",\n  "parameters": { }\n}\n```\n\n';
    }
    if (hasSaipos) {
      prompt += '```tool\n{\n  "tool": "saipos",\n  "operation": "OPERACAO_AQUI",\n  "parameters": { }\n}\n```\n\n';
    }

    for (const tool of availableTools) {
      prompt += `### 🔧 ${tool.name}\n`;
      prompt += `${tool.description}\n\n`;
      prompt += `**Operações disponíveis:**\n\n`;
      
      for (const op of tool.operations) {
        prompt += `- **${op.name}** (operation: \`${op.id}\`)\n`;
        prompt += `  ${op.description}\n`;
        
        if (op.parameters.length > 0) {
          prompt += '  Parâmetros:\n';
          for (const param of op.parameters) {
            const required = param.required ? '⚡OBRIGATÓRIO' : 'opcional';
            prompt += `    - \`${param.name}\` (${param.type}) [${required}]: ${param.description}\n`;
          }
        }
        prompt += '\n';
      }
    }

    // Exemplos específicos por ferramenta
    if (hasCalendar) {
      prompt += '\n📌 **QUANDO O USUÁRIO PERGUNTAR SOBRE CALENDÁRIO, RESPONDA ASSIM:**\n\n';
      prompt += '❓ Pergunta: "Quais são meus eventos de hoje?" ou "O que tenho na agenda?"\n';
      prompt += '✅ Resposta CORRETA:\n';
      prompt += '```tool\n{"tool": "google_calendar", "operation": "list_events", "parameters": {"maxResults": 10}}\n```\n\n';
      prompt += '❓ Pergunta: "Crie uma reunião" ou "Agende um compromisso"\n';
      prompt += '✅ Resposta CORRETA:\n';
      prompt += `\`\`\`tool\n{"tool": "google_calendar", "operation": "create_event", "parameters": {"summary": "Nome do evento", "start": "${isoDate}T14:00:00", "end": "${isoDate}T15:00:00"}}\n\`\`\`\n\n`;
    }
    
    if (hasSaipos) {
      prompt += '\n📌 **QUANDO O USUÁRIO PERGUNTAR SOBRE VENDAS, ESTOQUE OU CARDÁPIO (SAIPOS), RESPONDA ASSIM:**\n\n';
      prompt += '❓ Pergunta: "Quanto vendi ontem?" ou "Vendas de ontem" ou "Faturamento"\n';
      prompt += '✅ Resposta CORRETA:\n';
      prompt += `\`\`\`tool\n{"tool": "saipos", "operation": "get_sales", "parameters": {"date_from": "${isoDate}", "date_to": "${isoDate}"}}\n\`\`\`\n\n`;
      prompt += '❓ Pergunta: "Quais itens foram vendidos?" ou "Itens das vendas" ou "Produtos vendidos"\n';
      prompt += '✅ Resposta CORRETA:\n';
      prompt += `\`\`\`tool\n{"tool": "saipos", "operation": "get_sale_items", "parameters": {"date_from": "${isoDate}", "date_to": "${isoDate}"}}\n\`\`\`\n`;
      prompt += '(No retorno de get_sale_items use: quantity = quantidade, unit_price = valor, total vendido = quantity × unit_price.)\n\n';
      prompt += '❓ Pergunta: "Consulte meu estoque disponível" ou "Estoque atual"\n';
      prompt += '✅ Resposta CORRETA:\n';
      prompt += '```tool\n{"tool": "saipos", "operation": "get_stock_position", "parameters": {}}\n```\n\n';
      prompt += '❓ Pergunta: "Estoque na última semana" ou "posição de estoque nos últimos 7 dias"\n';
      prompt += '✅ Resposta CORRETA:\n';
      prompt += '```tool\n{"tool": "saipos", "operation": "get_stock_position", "parameters": {"days": 7}}\n```\n\n';
      prompt += '❓ Pergunta: "Qual o cardápio?" ou "Consultar cardápio" ou "Menu disponível"\n';
      prompt += '✅ Resposta CORRETA:\n';
      prompt += '```tool\n{"tool": "saipos", "operation": "get_catalog", "parameters": {}}\n```\n\n';
    }

    prompt += '═'.repeat(80) + '\n';
    if (hasCalendar) {
      prompt += '🚨 SE A PERGUNTA FOR SOBRE CALENDÁRIO/EVENTOS/AGENDA → USE ```tool\n';
    }
    if (hasSaipos) {
      prompt += '🚨 SE A PERGUNTA FOR SOBRE VENDAS, ESTOQUE OU CARDÁPIO (SAIPOS) → USE ```tool\n';
    }
    prompt += '🚨 NÃO GERE HTML, PDF, CSV OU QUALQUER ARQUIVO PARA DOWNLOAD!\n';
    prompt += '🚨 APENAS USE A FERRAMENTA E MOSTRE O RESULTADO EM TEXTO!\n';
    prompt += '═'.repeat(80) + '\n\n';

    return prompt;
  }

  // Extrair chamadas de ferramentas da resposta da IA
  extractToolCalls(content: string): Array<{
    tool: string;
    operation: string;
    parameters: Record<string, any>;
    rawBlock: string;
  }> {
    const toolCalls: Array<{
      tool: string;
      operation: string;
      parameters: Record<string, any>;
      rawBlock: string;
    }> = [];

    // Regex para encontrar blocos ```tool
    const toolBlockRegex = /```tool\s*([\s\S]*?)```/g;
    let match;

    while ((match = toolBlockRegex.exec(content)) !== null) {
      try {
        const jsonStr = match[1].trim();
        const parsed = JSON.parse(jsonStr);
        
        if (parsed.tool && parsed.operation) {
          toolCalls.push({
            tool: parsed.tool,
            operation: parsed.operation,
            parameters: parsed.parameters || {},
            rawBlock: match[0]
          });
        }
      } catch (e) {
        console.error('Erro ao parsear bloco de ferramenta:', e);
      }
    }

    return toolCalls;
  }

  // Executar uma operação de ferramenta
  async executeOperation(
    toolId: string,
    operationId: string,
    parameters: Record<string, any>,
    context: ToolExecutionContext
  ): Promise<ToolCallResult> {
    const startedAt = new Date();
    
    const result: ToolCallResult = {
      toolId,
      operationId,
      operationName: this.getOperationName(toolId, operationId),
      parameters,
      status: 'running',
      startedAt
    };

    // Verificar se a operação está habilitada
    if (!this.isOperationEnabled(toolId, operationId, context.assistantTools)) {
      result.status = 'failed';
      result.error = 'Operação não habilitada para este assistente';
      result.completedAt = new Date();
      return result;
    }

    try {
      switch (toolId) {
        case 'google_calendar':
          result.result = await this.executeCalendarOperation(operationId, parameters, context.userId);
          break;
        case 'google_drive':
          result.result = await this.executeDriveOperation(operationId, parameters, context.userId);
          break;
        case 'saipos':
          result.result = await this.executeSaiposOperation(operationId, parameters, context.userId);
          break;
        default:
          throw new Error(`Ferramenta não suportada: ${toolId}`);
      }
      
      result.status = 'completed';
    } catch (error: any) {
      result.status = 'failed';
      result.error = error.message || 'Erro ao executar operação';
    }

    result.completedAt = new Date();
    return result;
  }

  private getOperationName(toolId: string, operationId: string): string {
    const tool = AVAILABLE_TOOLS.find(t => t.id === toolId);
    const operation = tool?.operations.find(op => op.id === operationId);
    return operation?.name || operationId;
  }

  // Executar operações do Google Calendar
  private async executeCalendarOperation(
    operationId: string,
    parameters: Record<string, any>,
    userId: string
  ): Promise<any> {
    switch (operationId) {
      case 'list_calendars':
        return await googleCalendarService.listCalendars(userId);

      case 'list_events':
        return await googleCalendarService.listEvents(userId, {
          calendarId: parameters.calendarId,
          timeMin: parameters.timeMin,
          timeMax: parameters.timeMax,
          maxResults: parameters.maxResults,
          query: parameters.query
        });

      case 'create_event':
        return await googleCalendarService.createEvent(userId, {
          summary: parameters.summary,
          description: parameters.description,
          start: parameters.start,
          end: parameters.end,
          attendees: parameters.attendees,
          location: parameters.location,
          calendarId: parameters.calendarId
        });

      case 'update_event':
        return await googleCalendarService.updateEvent(userId, parameters.eventId, {
          summary: parameters.summary,
          description: parameters.description,
          start: parameters.start,
          end: parameters.end,
          attendees: parameters.attendees,
          location: parameters.location,
          calendarId: parameters.calendarId
        });

      case 'delete_event':
        return await googleCalendarService.deleteEvent(
          userId,
          parameters.eventId,
          parameters.calendarId
        );

      case 'find_free_slots':
        return await googleCalendarService.findFreeSlots(userId, {
          attendees: parameters.attendees,
          duration: parameters.duration,
          timeMin: parameters.timeMin,
          timeMax: parameters.timeMax
        });

      default:
        throw new Error(`Operação não suportada: ${operationId}`);
    }
  }

  // Executar operações do Google Drive
  private async executeDriveOperation(
    operationId: string,
    parameters: Record<string, any>,
    userId: string
  ): Promise<any> {
    switch (operationId) {
      case 'select_file':
        const files = await googleDriveService.selectAndDownloadFiles(userId);
        return files.map(f => ({
          name: f.name,
          size: f.size,
          type: f.type
        }));

      case 'list_files':
        // Por enquanto, abrir o picker
        const selectedFiles = await googleDriveService.openPicker(userId);
        return selectedFiles;

      default:
        throw new Error(`Operação não suportada: ${operationId}`);
    }
  }

  // Executar operações do Saipos
  private async executeSaiposOperation(
    operationId: string,
    parameters: Record<string, any>,
    userId: string
  ): Promise<any> {
    switch (operationId) {
      case 'get_sales': {
        const r = await saiposService.getSales(userId, {
          store_id: parameters.store_id,
          sale_id: parameters.sale_id,
          date_from: parameters.date_from,
          date_to: parameters.date_to,
          status: parameters.status,
          customer_document: parameters.customer_document,
          limit: parameters.limit,
          offset: parameters.offset
        });
        return r.data;
      }
      case 'get_sale_items': {
        const r = await saiposService.getSaleItems(userId, {
          date_from: parameters.date_from,
          date_to: parameters.date_to,
          date_column_filter: parameters.date_column_filter,
          limit: parameters.limit,
          offset: parameters.offset
        });
        return r.data;
      }
      case 'get_stock_position': {
        const r = await saiposService.getStockPosition(userId, {
          store_id: parameters.store_id,
          ingredient_id: parameters.ingredient_id,
          days: parameters.days
        });
        return r.data;
      }
      case 'get_catalog': {
        const r = await saiposService.getCatalog(userId);
        return r.data;
      }

      default:
        throw new Error(`Operação não suportada: ${operationId}`);
    }
  }

  // Formatar resultado para mostrar na resposta
  formatResultForDisplay(result: ToolCallResult): string {
    if (result.status === 'failed') {
      return `❌ Erro: ${result.error}`;
    }

    if (!result.result) {
      return '✅ Operação concluída';
    }

    // Formatação específica por tipo de resultado
    if (Array.isArray(result.result)) {
      if (result.result.length === 0) {
        return '📭 Nenhum resultado encontrado';
      }

      // Para eventos do calendário
      if (result.result[0]?.summary !== undefined) {
        return result.result.map((event: any) => {
          const start = new Date(event.start?.dateTime || event.start?.date);
          return `📅 **${event.summary}** - ${start.toLocaleDateString('pt-BR')} às ${start.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
        }).join('\n');
      }

      // Para calendários
      if (result.result[0]?.id && result.result[0]?.summary !== undefined) {
        return result.result.map((cal: any) => 
          `📆 ${cal.summary}${cal.primary ? ' (Principal)' : ''}`
        ).join('\n');
      }

      // Para slots livres
      if (result.result[0]?.start && result.result[0]?.end) {
        return result.result.map((slot: any) => {
          const start = new Date(slot.start);
          const end = new Date(slot.end);
          return `⏰ ${start.toLocaleDateString('pt-BR')} das ${start.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })} às ${end.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
        }).join('\n');
      }
    }

    // Para evento único criado/atualizado
    if (result.result.summary && result.result.start) {
      const start = new Date(result.result.start?.dateTime || result.result.start?.date);
      return `✅ Evento "${result.result.summary}" ${result.operationId === 'create_event' ? 'criado' : 'atualizado'} para ${start.toLocaleDateString('pt-BR')} às ${start.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
    }

    // Para deleção
    if (result.result === true) {
      return '✅ Operação concluída com sucesso';
    }

    return JSON.stringify(result.result, null, 2);
  }
}

export const toolExecutorService = new ToolExecutorService();

